import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { trackEvent } from '../utils/facebookPixel';

// Styled Components
const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background-color: #f0f2f5;
  padding: 2rem;
`;

const Card = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 500px;
  text-align: center;
`;

const WarningIcon = styled.div`
  font-size: 4rem;
  color: #f59e0b;
  margin-bottom: 1rem;
`;

const Title = styled.h2`
  color: #333;
  margin-bottom: 1rem;
`;

const Message = styled.p`
  color: #666;
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const Button = styled.button`
  padding: 1rem 2rem;
  background-color: #043663;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1.1rem;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;
  margin-right: 1rem;

  &:hover {
    background: #032a4a;
  }
`;

const SecondaryButton = styled.button`
  padding: 1rem 2rem;
  background-color: #6c757d;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1.1rem;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;

  &:hover {
    background: #5a6268;
  }
`;

const PaymentCancelledPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isPlayerFlow = location.pathname.startsWith('/player/');
  const [cleaning, setCleaning] = useState(true);
  const [cleanupComplete, setCleanupComplete] = useState(false);
  const teamId = searchParams.get('team_id');
  const registrationId = searchParams.get('registration_id');
  const isPreview = searchParams.get('preview') === 'true';

  useEffect(() => {
    if (teamId) {
      cleanupTeam();
    } else {
      setCleaning(false);
      setCleanupComplete(true);
    }
  }, [teamId]);

  // Track abandoned cart when payment is cancelled (legacy team_id or Phase 2 invoice_id)
  useEffect(() => {
    const invoiceId = searchParams.get('invoice_id');
    if (!cleanupComplete) return;
    if (!teamId && !invoiceId) return;
    trackEvent('AddToCart', {
      content_name: 'Team Registration - Abandoned',
      content_category: 'Registration',
      value: 1,
      currency: 'CHF',
      content_ids: [teamId || invoiceId],
      custom_data: {
        team_id: teamId || null,
        invoice_id: invoiceId || null,
        registration_stage: 'payment_cancelled',
        abandonment_type: 'checkout_abandoned',
      },
    });
  }, [cleanupComplete, teamId, searchParams]);

  // Send message to parent window if opened in new tab
  useEffect(() => {
    if (window.opener && !isPreview && cleanupComplete) {
      window.opener.postMessage({ type: 'PAYMENT_CANCELLED', teamId }, window.location.origin);
      // Close the payment tab after 2 seconds
      setTimeout(() => {
        window.close();
      }, 2000);
    }
  }, [cleanupComplete, teamId, isPreview]);

  const cleanupTeam = async () => {
    try {
      console.log('Cleaning up cancelled team:', teamId);
      
      // Call cleanup API
      await api.post('/public-payments/cleanup-cancelled', {
        team_id: teamId,
        registration_id: registrationId
      });
      
      console.log('Team cleanup completed');
      setCleanupComplete(true);
    } catch (error) {
      console.error('Error cleaning up team:', error);
      // Continue anyway - don't block user
      setCleanupComplete(true);
    } finally {
      setCleaning(false);
    }
  };

  if (cleaning) {
    return (
      <PageContainer>
        <Card>
          <WarningIcon>⚠️</WarningIcon>
          <Title>{t('paymentFlows.paymentCancelledPage.title')}</Title>
          <Message>{t('paymentFlows.paymentCancelledPage.cleaningUp')}</Message>
        </Card>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Card>
        <WarningIcon>⚠️</WarningIcon>
        <Title>{t('paymentFlows.paymentCancelledPage.title')}</Title>
        <Message>
          {t('paymentFlows.paymentCancelledPage.bodyMain')}{' '}
          {window.opener && !isPreview
            ? t('paymentFlows.paymentCancelledPage.hintOpener')
            : t('paymentFlows.paymentCancelledPage.hintStandalone')}
        </Message>
        <div>
          <Button onClick={() => navigate(isPlayerFlow ? '/player/tournaments' : '/register-team')}>
            {isPlayerFlow
              ? t('paymentFlows.paymentCancelledPage.backToTournaments')
              : t('paymentFlows.paymentCancelledPage.registerNewTeam')}
          </Button>
          <SecondaryButton onClick={() => navigate(isPlayerFlow ? '/player' : '/')}>
            {isPlayerFlow ? t('paymentFlows.paymentCancelledPage.playerHome') : t('paymentFlows.paymentCancelledPage.goHome')}
          </SecondaryButton>
        </div>
      </Card>
    </PageContainer>
  );
};

export default PaymentCancelledPage;
